import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Select, message, Tabs, Modal, Alert, Steps } from 'antd';
import { ArrowRightOutlined, CloudOutlined, AmazonOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCloud } from '../context/CloudContext';
import apiClient from '../services/apiClient';
import './ConnectClouds.css';

const { Option } = Select;
const { TabPane } = Tabs;
const AZURE_DEVICE_LOGIN_URL = 'https://microsoft.com/devicelogin';
const UNKNOWN_AZURE_DIRECTORY = '__unknown_directory__';

const getAzureSubscriptionId = (subscription: any) => subscription.subscriptionId || subscription.id;

const getAzureDirectoryId = (subscription: any) =>
  subscription.tenantId || subscription.directoryId || subscription.azureTenantId || UNKNOWN_AZURE_DIRECTORY;

const getAzureDirectoryName = (subscription: any) =>
  subscription.tenantName || subscription.directoryName || subscription.tenantDisplayName || 'Directory não identificado';

const ConnectClouds: React.FC = () => {
  const navigate = useNavigate();
  const { setConnection } = useCloud();
  const [loading, setLoading] = useState(false);
  
  const savedAwsConfig = (() => {
    try {
      return JSON.parse(localStorage.getItem('awsConfig') || '{}');
    } catch (error) {
      return {};
    }
  })();
  const [awsCustomerName, setAwsCustomerName] = useState(savedAwsConfig.customerName || '');
  const [awsCustomerDomain, setAwsCustomerDomain] = useState(savedAwsConfig.customerDomain || '');
  const [awsRoleArn, setAwsRoleArn] = useState(savedAwsConfig.roleArn || '');
  const [awsExternalId, setAwsExternalId] = useState('');
  const [awsPrincipalArn, setAwsPrincipalArn] = useState('');
  const [awsOnboardingError, setAwsOnboardingError] = useState('');
  const [awsConnectionError, setAwsConnectionError] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');
  const [awsMode, setAwsMode] = useState<'individual' | 'organization'>('individual');
  const [awsOrganizationId, setAwsOrganizationId] = useState('');
  const [awsManagementAccountId, setAwsManagementAccountId] = useState('');
  const [awsOrganizationOnboarding, setAwsOrganizationOnboarding] = useState<any>(null);
  const [awsOrganizationAccounts, setAwsOrganizationAccounts] = useState<any[]>([]);
  const [selectedAwsOrganizationAccounts, setSelectedAwsOrganizationAccounts] = useState<string[]>([]);
  
  // Azure State
  const [azureModalVisible, setAzureModalVisible] = useState(false);
  const [deviceCodeData, setDeviceCodeData] = useState<any>(null);
  const [polling, setPolling] = useState(false);
  const [azureAuthCompleted, setAzureAuthCompleted] = useState(false);
  const [azureSubscriptions, setAzureSubscriptions] = useState<any[]>([]);
  const [selectedAzureSubscriptions, setSelectedAzureSubscriptions] = useState<string[]>([]);
  const [selectedAzureDirectory, setSelectedAzureDirectory] = useState<string>('');
  const [azureCustomerName, setAzureCustomerName] = useState('');
  const [azureCustomerDomain, setAzureCustomerDomain] = useState('');
  const [savingAzureSubscriptions, setSavingAzureSubscriptions] = useState(false);
  const [gcpProjectId, setGcpProjectId] = useState('');
  const [gcpWorkloadIdentityProvider, setGcpWorkloadIdentityProvider] = useState('');
  const [gcpServiceAccountEmail, setGcpServiceAccountEmail] = useState('');
  const [gcpBillingExportProjectId, setGcpBillingExportProjectId] = useState('');
  const [gcpBillingExportDataset, setGcpBillingExportDataset] = useState('');
  const [gcpBillingExportTable, setGcpBillingExportTable] = useState('');
  const [gcpCustomerName, setGcpCustomerName] = useState('');
  const [gcpCustomerDomain, setGcpCustomerDomain] = useState('');
  const [gcpScopeType, setGcpScopeType] = useState<'project' | 'folder' | 'organization'>('project');
  const [gcpScopeId, setGcpScopeId] = useState('');
  const [gcpDiscoveredProjects, setGcpDiscoveredProjects] = useState<any[]>([]);
  const [gcpSelectedProjectIds, setGcpSelectedProjectIds] = useState<string[]>([]);
  const [discoveringGcpProjects, setDiscoveringGcpProjects] = useState(false);
  const [gcpSetupProjectId, setGcpSetupProjectId] = useState('');
  const [gcpSetupBillingAccountId, setGcpSetupBillingAccountId] = useState('');
  const [gcpSetupPoolId, setGcpSetupPoolId] = useState('xknow-finops-pool');
  const [gcpSetupProviderId, setGcpSetupProviderId] = useState('xknow-aws-provider');
  const [gcpSetupServiceAccountEmail, setGcpSetupServiceAccountEmail] = useState('');
  const [gcpOnboarding, setGcpOnboarding] = useState<any>(null);
  const [gcpOnboardingError, setGcpOnboardingError] = useState('');
  const [gcpStep, setGcpStep] = useState(0);

  useEffect(() => {
    const roleArn = awsRoleArn.trim();
    if (!roleArn) {
      setAwsExternalId('');
      setAwsPrincipalArn('');
      setAwsOnboardingError('');
      return;
    }

    const timeout = window.setTimeout(() => {
      apiClient.get('/cloud/aws/onboarding', { params: { roleArn } })
        .then(response => {
          setAwsExternalId(response.data.externalId);
          setAwsPrincipalArn(response.data.principalArn);
          setAwsOnboardingError('');
        })
        .catch((error: any) => {
          setAwsExternalId('');
          setAwsPrincipalArn('');
          setAwsOnboardingError(error.response?.data?.message || 'Não foi possível gerar o External ID.');
        });
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [awsRoleArn]);

  useEffect(() => {
    if (awsMode !== 'organization' || !/^o-[a-z0-9]{10,32}$/.test(awsOrganizationId.trim())) {
      setAwsOrganizationOnboarding(null);
      return;
    }
    const timeout = window.setTimeout(() => {
      apiClient.get('/cloud/aws/organization/onboarding', {
        params: { organizationId: awsOrganizationId.trim() }
      }).then(response => {
        setAwsOrganizationOnboarding(response.data);
        setAwsOnboardingError('');
      }).catch((error: any) => {
        setAwsOrganizationOnboarding(null);
        setAwsOnboardingError(error.response?.data?.message || 'Não foi possível preparar o StackSet.');
      });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [awsMode, awsOrganizationId]);

  useEffect(() => {
    apiClient.get('/cloud/gcp/onboarding')
      .then(response => {
        setGcpOnboarding(response.data);
        setGcpOnboardingError('');
      })
      .catch((error: any) => {
        setGcpOnboarding(null);
        setGcpOnboardingError(error.response?.data?.error || 'Não foi possível carregar a configuração segura do GCP.');
      });
  }, []);

  const handleAWSConnect = async () => {
    setLoading(true);
    setAwsConnectionError('');
    try {
      const payload: any = {
        region: awsRegion,
        customerName: awsCustomerName.trim() || undefined,
        customerDomain: awsCustomerDomain.trim() || undefined
      };
      const roleArn = awsRoleArn.trim();
      const externalId = awsExternalId.trim();
      
      if (!roleArn || !externalId) {
        const errorMessage = 'Role ARN e External ID são obrigatórios';
        setAwsConnectionError(errorMessage);
        message.error(errorMessage);
        return;
      }
      payload.roleArn = roleArn;

      const response = await apiClient.post('/cloud/aws/connect', payload);
      
      if (response.data.success) {
        message.success('Conectado à AWS com sucesso!');
        const nextAwsData = {
          ...(response.data.data || {}),
          cloudAccountId: response.data.cloudAccount?.id,
          cloudAccount: response.data.cloudAccount
        };
        
        // Salvar dados AWS no localStorage
        localStorage.setItem('awsData', JSON.stringify(nextAwsData));
        localStorage.setItem('awsConnected', 'true');
        localStorage.setItem('awsCloudAccountId', response.data.cloudAccount?.id || '');
        localStorage.setItem('awsConfig', JSON.stringify({
          roleArn,
          region: awsRegion,
          customerName: awsCustomerName.trim(),
          customerDomain: awsCustomerDomain.trim()
        }));
        setConnection('aws', {
          provider: 'aws',
          connected: true,
          cloudAccountId: response.data.cloudAccount?.id,
          accountId: response.data.cloudAccount?.accountId || response.data.data?.account?.id
        });
        
        // Redirecionar para dashboard AWS
        setTimeout(() => {
          const cloudAccountId = response.data.cloudAccount?.id;
          const reportId = response.data.report?.id || response.data.data?.reportId;
          navigate(
            `/aws-dashboard?cloudAccountId=${encodeURIComponent(cloudAccountId || '')}` +
            (reportId ? `&reportId=${encodeURIComponent(reportId)}` : '')
          );
        }, 1000);
      } else {
        const errorMessage = response.data.message || 'Erro ao conectar AWS';
        setAwsConnectionError(errorMessage);
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Erro AWS:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao conectar com AWS';
      setAwsConnectionError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const discoverAwsOrganization = async () => {
    setLoading(true);
    setAwsConnectionError('');
    try {
      const response = await apiClient.post('/cloud/aws/organization/discover', {
        organizationId: awsOrganizationId.trim(),
        managementAccountId: awsManagementAccountId.trim()
      });
      setAwsOrganizationAccounts(response.data.accounts || []);
      setSelectedAwsOrganizationAccounts((response.data.accounts || []).map((account: any) => account.id));
      message.success(`${response.data.accounts?.length || 0} contas AWS descobertas.`);
    } catch (error: any) {
      setAwsConnectionError(error.response?.data?.message || 'Não foi possível descobrir as contas da organização.');
    } finally {
      setLoading(false);
    }
  };

  const connectAwsOrganization = async () => {
    setLoading(true);
    setAwsConnectionError('');
    try {
      const response = await apiClient.post('/cloud/aws/organization/connect', {
        organizationId: awsOrganizationId.trim(),
        managementAccountId: awsManagementAccountId.trim(),
        accountIds: selectedAwsOrganizationAccounts,
        region: awsRegion,
        customerName: awsCustomerName.trim(),
        customerDomain: awsCustomerDomain.trim()
      });
      message.success(`${response.data.connected || 0} contas conectadas; ${response.data.failed || 0} falharam.`);
      if (response.data.failed > 0) {
        const failures = (response.data.results || [])
          .filter((result: any) => !result.success)
          .map((result: any) => `${result.accountId}: ${result.message}`)
          .join(' | ');
        setAwsConnectionError(failures || 'Algumas contas não puderam ser conectadas.');
      } else {
        navigate('/connect-clouds');
      }
    } catch (error: any) {
      setAwsConnectionError(error.response?.data?.message || 'Não foi possível conectar a organização AWS.');
    } finally {
      setLoading(false);
    }
  };

  const handleAzureConnect = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post('/cloud/azure/device-code');
      
      if (response.data.success) {
        setDeviceCodeData(response.data);
        startPolling(response.data.deviceCode);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao iniciar autenticação');
    }
    setLoading(false);
  };
  
  const startPolling = (deviceCode: string) => {
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.post('/cloud/azure/device-status', {
          deviceCode
        });
        
        if (response.data.status === 'completed') {
          clearInterval(pollInterval);
          setPolling(false);
          setAzureAuthCompleted(true);
          const subscriptions = response.data.subscriptions || response.data.cloudAccounts || [];
          const firstDirectory = subscriptions.length ? getAzureDirectoryId(subscriptions[0]) : '';
          const subscriptionIds = subscriptions
            .filter((subscription: any) => !firstDirectory || getAzureDirectoryId(subscription) === firstDirectory)
            .map(getAzureSubscriptionId)
            .filter(Boolean);
          setAzureSubscriptions(subscriptions);
          setSelectedAzureDirectory(firstDirectory);
          setSelectedAzureSubscriptions(subscriptionIds);
          message.success('Login Azure realizado. Escolha o Directory e as subscriptions para conectar.');
        } else if (response.data.status === 'expired') {
          clearInterval(pollInterval);
          setPolling(false);
          throw new Error('Código expirado. Tente novamente.');
        }
      } catch (error: any) {
        clearInterval(pollInterval);
        setPolling(false);
        const details = error.response?.data;
        const requestId = details?.requestId ? ` Código de suporte: ${details.requestId}` : '';
        message.error(`${details?.message || error.message || 'Erro na autenticação'}${requestId}`);
      }
    }, 5000);
  };

  const handleSelectAllAzureSubscriptions = () => {
    setSelectedAzureSubscriptions(
      azureSubscriptions
        .filter(subscription => !selectedAzureDirectory || getAzureDirectoryId(subscription) === selectedAzureDirectory)
        .map(getAzureSubscriptionId)
        .filter(Boolean)
    );
  };

  const handleClearAzureSubscriptions = () => {
    setSelectedAzureSubscriptions([]);
  };

  const handleSelectAzureSubscriptions = async () => {
    const selectedSubscriptions = azureSubscriptions.filter(item =>
      selectedAzureSubscriptions.includes(getAzureSubscriptionId(item))
    );

    if (!selectedSubscriptions.length) {
      message.warning('Selecione ao menos uma subscription Azure para continuar.');
      return;
    }

    setSavingAzureSubscriptions(true);

    try {
      const responses = await Promise.all(selectedSubscriptions.map(subscription => {
        const subscriptionId = getAzureSubscriptionId(subscription);
        const directoryId = getAzureDirectoryId(subscription);
        const directoryName = getAzureDirectoryName(subscription);
        return apiClient.post('/cloud/accounts', {
          provider: 'azure',
          name: subscription.name || subscription.displayName || subscriptionId || 'Azure Subscription',
          subscriptionId,
          connectionMethod: 'device-code',
          metadata: {
            azureState: subscription.state,
            azureTenantId: directoryId === UNKNOWN_AZURE_DIRECTORY ? undefined : directoryId,
            azureTenantName: directoryName,
            azureDirectoryId: directoryId === UNKNOWN_AZURE_DIRECTORY ? undefined : directoryId,
            azureDirectoryName: directoryName,
            customerName: azureCustomerName.trim(),
            customerDomain: azureCustomerDomain.trim(),
            lastRefreshAt: new Date().toISOString()
          }
        });
      }));

      const connectedAccounts = responses.map(response => response.data?.account).filter(Boolean);
      const firstAccount = connectedAccounts[0];
      const firstSubscription = selectedSubscriptions[0];
      const firstSubscriptionId = firstAccount?.subscriptionId || firstSubscription.subscriptionId || firstSubscription.id;

      localStorage.setItem('selectedSubscription', firstSubscriptionId);
      localStorage.setItem('azureSelectedSubscriptions', JSON.stringify(
        connectedAccounts.map(account => account.subscriptionId).filter(Boolean)
      ));
      localStorage.setItem('azureCloudAccountId', firstAccount?.id || '');
      setConnection('azure', {
        provider: 'azure',
        connected: true,
        cloudAccountId: firstAccount?.id,
        cloudAccountIds: connectedAccounts.map(account => account.id).filter(Boolean),
        subscriptionId: firstSubscriptionId,
        subscriptionIds: connectedAccounts.map(account => account.subscriptionId).filter(Boolean),
        accountId: firstSubscriptionId
      });

      message.success(`${connectedAccounts.length} ambiente(s) Azure conectado(s).`);
      setAzureModalVisible(false);
      setDeviceCodeData(null);
      setAzureAuthCompleted(false);
      setAzureSubscriptions([]);
      setSelectedAzureSubscriptions([]);
      setSelectedAzureDirectory('');
      setAzureCustomerName('');
      setAzureCustomerDomain('');
      navigate('/');
    } catch (error: any) {
      console.error('Erro ao salvar subscriptions Azure:', error);
      message.error(
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Não foi possível salvar as subscriptions selecionadas.'
      );
    } finally {
      setSavingAzureSubscriptions(false);
    }
  };

  const handleGCPConnect = async () => {
    const projectId = gcpProjectId.trim();
    const workloadIdentityProvider = gcpWorkloadIdentityProvider.trim();
    const serviceAccountEmail = gcpServiceAccountEmail.trim();
    const projectIds = gcpScopeType === 'project'
      ? [projectId].filter(Boolean)
      : gcpSelectedProjectIds;

    if (!projectIds.length || !workloadIdentityProvider || !serviceAccountEmail) {
      message.error('Selecione ao menos um projeto, Workload Identity Provider e Service Account Email.');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/cloud/gcp/connect', {
        projectId: projectIds[0],
        projectIds,
        scopeType: gcpScopeType,
        scopeId: gcpScopeType === 'project' ? projectIds[0] : gcpScopeId.trim(),
        workloadIdentityProvider,
        serviceAccountEmail,
        billingAccountId: gcpSetupBillingAccountId.trim() || undefined,
        customerName: gcpCustomerName.trim() || undefined,
        customerDomain: gcpCustomerDomain.trim() || undefined,
        billingExportProjectId: hasInvalidGcpBillingExportProjectId ? undefined : gcpBillingExportProjectId.trim() || undefined,
        billingExportDataset: gcpBillingExportDataset.trim() || undefined,
        billingExportTable: gcpBillingExportTable.trim() || undefined
      });

      if (!response.data?.success) {
        message.error(response.data?.message || 'Erro ao conectar Google Cloud.');
        return;
      }

      const cloudAccount = response.data.cloudAccount;
      localStorage.setItem('gcpCloudAccountId', cloudAccount?.id || '');
      localStorage.setItem('gcpProjectId', cloudAccount?.accountId || projectIds[0]);
      setConnection('gcp', {
        provider: 'gcp',
        connected: true,
        cloudAccountId: cloudAccount?.id,
        accountId: cloudAccount?.accountId || projectIds[0]
      });

      message.success(response.data.message || 'Google Cloud conectado.');
      if (response.data?.warnings?.length) {
        message.warning(response.data.warnings[0]);
      }
      navigate('/reports');
    } catch (error: any) {
      console.error('Erro GCP:', error);
      message.error(error.response?.data?.message || 'Erro ao conectar Google Cloud.');
    } finally {
      setLoading(false);
    }
  };

  const handleGCPDiscoverProjects = async () => {
    const workloadIdentityProvider = gcpWorkloadIdentityProvider.trim();
    const serviceAccountEmail = gcpServiceAccountEmail.trim();
    const scopeId = gcpScopeType === 'project' ? gcpProjectId.trim() : gcpScopeId.trim();

    if (!scopeId || !workloadIdentityProvider || !serviceAccountEmail) {
      message.error('Informe escopo, Workload Identity Provider e Service Account Email para descobrir projetos.');
      return;
    }

    setDiscoveringGcpProjects(true);
    try {
      const response = await apiClient.post('/cloud/gcp/discover-projects', {
        scopeType: gcpScopeType,
        scopeId,
        projectId: gcpProjectId.trim() || undefined,
        workloadIdentityProvider,
        serviceAccountEmail
      });
      const projects = response.data?.projects || [];
      setGcpDiscoveredProjects(projects);
      setGcpSelectedProjectIds(projects.map((project: any) => project.projectId).filter(Boolean));
      message.success(`${projects.length} projeto(s) encontrado(s).`);
    } catch (error: any) {
      console.error('Erro ao descobrir projetos GCP:', error);
      message.error(error.response?.data?.message || 'Não foi possível descobrir projetos GCP.');
    } finally {
      setDiscoveringGcpProjects(false);
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Código copiado!');
  };

  const handleGcpSetupServiceAccountEmailChange = (value: string) => {
    setGcpSetupServiceAccountEmail(value);
    setGcpServiceAccountEmail(value);
  };

  const handleGcpServiceAccountEmailChange = (value: string) => {
    setGcpServiceAccountEmail(value);
    setGcpSetupServiceAccountEmail(value);
  };

  const isValidGcpProjectId = (value: string) =>
    /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value.trim());

  const shellQuote = (value: string) => `'${String(value || '').replace(/'/g, `'\\''`)}'`;

  const normalizeGcpServiceAccountEmail = (value: string, projectId: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return `xknow-finops-readonly@${projectId}.iam.gserviceaccount.com`;
    }
    if (trimmed.includes('@')) {
      return trimmed;
    }
    return `${trimmed}@${projectId}.iam.gserviceaccount.com`;
  };

  const buildGcpSetupScript = () => {
    const projectId = gcpSetupProjectId.trim() || 'SEU_PROJECT_ID';
    const billingAccountId = gcpSetupBillingAccountId.trim();
    const poolId = gcpSetupPoolId.trim() || 'xknow-finops-pool';
    const providerId = gcpSetupProviderId.trim() || 'xknow-aws-provider';
    const serviceAccountEmail = normalizeGcpServiceAccountEmail(
      gcpSetupServiceAccountEmail.trim() || gcpServiceAccountEmail.trim(),
      projectId
    );
    const serviceAccountProjectId = serviceAccountEmail.split('@')[1]?.replace('.iam.gserviceaccount.com', '') || projectId;
    const serviceAccountId = serviceAccountEmail.split('@')[0] || 'xknow-finops-readonly';
    const billingExportProjectIdInput = gcpBillingExportProjectId.trim();
    const billingExportProjectId = billingExportProjectIdInput && isValidGcpProjectId(billingExportProjectIdInput)
      ? billingExportProjectIdInput
      : projectId;
    const scopeType = gcpScopeType;
    const scopeId = scopeType === 'project' ? projectId : gcpScopeId.trim();
    const awsAccountId = gcpOnboarding?.awsAccountId || '781024105587';
    const awsAssumedRoleArn = gcpOnboarding?.awsAssumedRoleArn || 'arn:aws:sts::781024105587:assumed-role/finops-pilot-app-task-role';
    const requiredApis = Array.from(new Set([
      ...(gcpOnboarding?.requiredApis || [
        'iamcredentials.googleapis.com',
        'sts.googleapis.com',
        'cloudresourcemanager.googleapis.com',
        'cloudbilling.googleapis.com',
        'bigquery.googleapis.com'
      ]),
      'compute.googleapis.com',
      'storage.googleapis.com',
      'sqladmin.googleapis.com',
      'container.googleapis.com',
      'monitoring.googleapis.com',
      'cloudasset.googleapis.com',
      'recommender.googleapis.com'
    ]));
    const projectRoles = [
      'roles/browser',
      'roles/cloudasset.viewer',
      'roles/monitoring.viewer',
      'roles/compute.viewer',
      'roles/storage.viewer',
      'roles/cloudsql.viewer',
      'roles/container.viewer',
      'roles/recommender.computeViewer',
      'roles/recommender.projectCudViewer'
    ];

    return `#!/usr/bin/env bash
if [[ $- == *i* ]]; then
  echo "ATENÇÃO: não cole este script diretamente no Cloud Shell."
  echo "Salve em um arquivo e execute com: bash xknow-gcp-onboarding.sh"
  echo "Assim erros não encerram a sessão interativa."
else
  set -Eeuo pipefail
  trap 'echo ""; echo "ERRO: script interrompido na linha $LINENO."; echo "Revise a mensagem acima e execute novamente com: bash xknow-gcp-onboarding.sh"' ERR
fi

# xKnow FinOps - GCP secure onboarding
# Rode este script com um usuário GCP administrador do onboarding.
# Permissões mínimas esperadas para o executor:
# - Service Usage Admin no projeto de setup, para habilitar APIs.
# - Service Account Admin no projeto de setup, para criar a SA e permitir Workload Identity.
# - Organization Admin/IAM Admin no escopo monitorado, para aplicar roles de leitura.
# - Billing Account Admin/IAM Admin na Billing Account, para aplicar roles/billing.viewer.
# - Project IAM Admin no projeto do Billing Export, para aplicar roles de BigQuery.

PROJECT_ID=${shellQuote(projectId)}
BILLING_ACCOUNT_ID=${shellQuote(billingAccountId)}
BILLING_EXPORT_PROJECT_ID=${shellQuote(billingExportProjectId)}
POOL_ID=${shellQuote(poolId)}
PROVIDER_ID=${shellQuote(providerId)}
SERVICE_ACCOUNT_ID=${shellQuote(serviceAccountId)}
SERVICE_ACCOUNT_EMAIL=${shellQuote(serviceAccountEmail)}
SERVICE_ACCOUNT_PROJECT_ID=${shellQuote(serviceAccountProjectId)}
AWS_ACCOUNT_ID=${shellQuote(awsAccountId)}
AWS_ASSUMED_ROLE_ARN=${shellQuote(awsAssumedRoleArn)}
AWS_ASSUMED_ROLE_PREFIX="$AWS_ASSUMED_ROLE_ARN/"

echo "0/6 Validando projeto e permissões básicas..."
if ! gcloud projects describe "$PROJECT_ID" >/dev/null 2>&1; then
  echo "ERRO: não foi possível acessar o projeto '$PROJECT_ID'."
  echo "Confirme se o Project ID está correto e se o usuário atual tem acesso."
  echo "Para testar em um GCP nosso, troque PROJECT_ID, SERVICE_ACCOUNT_PROJECT_ID e BILLING_EXPORT_PROJECT_ID para um projeto sandbox nosso."
  exit 1
fi

gcloud config set project "$PROJECT_ID"
PROJECT_NUMBER="$(gcloud projects describe "$PROJECT_ID" --format='value(projectNumber)')"
SERVICE_ACCOUNT_PROJECT_NUMBER="$(gcloud projects describe "$SERVICE_ACCOUNT_PROJECT_ID" --format='value(projectNumber)')"

echo "1/6 Habilitando APIs necessárias..."
gcloud services enable \\
${requiredApis.map((api: string) => `  ${api} \\`).join('\n')}
  --project "$PROJECT_ID"

echo "2/6 Criando Service Account read-only, se ainda não existir..."
gcloud iam service-accounts describe "$SERVICE_ACCOUNT_EMAIL" --project "$SERVICE_ACCOUNT_PROJECT_ID" >/dev/null 2>&1 || \\
  gcloud iam service-accounts create "$SERVICE_ACCOUNT_ID" \\
    --project "$SERVICE_ACCOUNT_PROJECT_ID" \\
    --display-name "xKnow FinOps Read Only"

echo "Aguardando propagação da Service Account no IAM..."
sleep 20

echo "3/6 Aplicando permissões read-only no projeto..."
if [ ${shellQuote(scopeType)} = "project" ]; then
${projectRoles.map(role => `  gcloud projects add-iam-policy-binding "$PROJECT_ID" \\
  --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
  --role "${role}" \\
  --condition=None \\
  --quiet`).join('\n\n')}
else
  SCOPE_TYPE=${shellQuote(scopeType)}
  SCOPE_ID=${shellQuote(scopeId)}
  if [ "$SCOPE_TYPE" = "folder" ]; then
${projectRoles.map(role => `    gcloud resource-manager folders add-iam-policy-binding "$SCOPE_ID" \\
      --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
      --role "${role}" \\
      --condition=None \\
      --quiet`).join('\n\n')}
  else
${projectRoles.map(role => `    gcloud organizations add-iam-policy-binding "$SCOPE_ID" \\
      --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
      --role "${role}" \\
      --condition=None \\
      --quiet`).join('\n\n')}
  fi
fi

if [ -n "$BILLING_ACCOUNT_ID" ]; then
  echo "4/6 Aplicando permissão de leitura na Billing Account..."
  gcloud beta billing accounts add-iam-policy-binding "$BILLING_ACCOUNT_ID" \\
    --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
    --role "roles/billing.viewer" \\
    --quiet
else
  echo "4/6 Billing Account não informada. Pulei roles/billing.viewer."
fi

if [ -n "$BILLING_EXPORT_PROJECT_ID" ]; then
  echo "4b/6 Aplicando permissões BigQuery no projeto do Billing Export..."
  gcloud services enable bigquery.googleapis.com --project "$BILLING_EXPORT_PROJECT_ID"
  gcloud projects add-iam-policy-binding "$BILLING_EXPORT_PROJECT_ID" \\
    --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
    --role "roles/bigquery.dataViewer" \\
    --condition=None \\
    --quiet
  gcloud projects add-iam-policy-binding "$BILLING_EXPORT_PROJECT_ID" \\
    --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
    --role "roles/bigquery.jobUser" \\
    --condition=None \\
    --quiet
fi

echo "5/6 Criando Workload Identity Pool e Provider AWS..."
gcloud iam workload-identity-pools describe "$POOL_ID" \\
  --project "$PROJECT_ID" \\
  --location "global" >/dev/null 2>&1 || \\
  gcloud iam workload-identity-pools create "$POOL_ID" \\
    --project "$PROJECT_ID" \\
    --location "global" \\
    --display-name "xKnow FinOps Federation"

if gcloud iam workload-identity-pools providers describe "$PROVIDER_ID" \\
  --project "$PROJECT_ID" \\
  --location "global" \\
  --workload-identity-pool "$POOL_ID" >/dev/null 2>&1; then
  echo "Provider já existe. Atualizando condição de segurança..."
  gcloud iam workload-identity-pools providers update-aws "$PROVIDER_ID" \\
    --project "$PROJECT_ID" \\
    --location "global" \\
    --workload-identity-pool "$POOL_ID" \\
    --account-id "$AWS_ACCOUNT_ID" \\
    --attribute-mapping "google.subject=assertion.arn,attribute.aws_role=assertion.arn" \\
    --attribute-condition "attribute.aws_role.startsWith('$AWS_ASSUMED_ROLE_PREFIX')" \\
    --display-name "xKnow AWS Provider"
else
  gcloud iam workload-identity-pools providers create-aws "$PROVIDER_ID" \\
    --project "$PROJECT_ID" \\
    --location "global" \\
    --workload-identity-pool "$POOL_ID" \\
    --account-id "$AWS_ACCOUNT_ID" \\
    --attribute-mapping "google.subject=assertion.arn,attribute.aws_role=assertion.arn" \\
    --attribute-condition "attribute.aws_role.startsWith('$AWS_ASSUMED_ROLE_PREFIX')" \\
    --display-name "xKnow AWS Provider"
fi

echo "6/6 Permitindo impersonation segura da Service Account pela xKnow..."
# A restrição da role AWS fica no provider, onde o GCP aceita attribute.aws_role.
gcloud iam service-accounts add-iam-policy-binding "$SERVICE_ACCOUNT_EMAIL" \\
  --project "$SERVICE_ACCOUNT_PROJECT_ID" \\
  --role "roles/iam.workloadIdentityUser" \\
  --member "principalSet://iam.googleapis.com/projects/$PROJECT_NUMBER/locations/global/workloadIdentityPools/$POOL_ID/*" \\
  --condition=None \\
  --quiet

WORKLOAD_IDENTITY_PROVIDER="//iam.googleapis.com/projects/$PROJECT_NUMBER/locations/global/workloadIdentityPools/$POOL_ID/providers/$PROVIDER_ID"

echo ""
echo "Concluído. Cole estes campos na xKnow:"
echo "Project ID: $PROJECT_ID"
echo "Workload Identity Provider: $WORKLOAD_IDENTITY_PROVIDER"
echo "Service Account Email: $SERVICE_ACCOUNT_EMAIL"
echo ""
echo "Importante: para dashboard financeiro completo, configure Cloud Billing Export para BigQuery e informe dataset/tabela na xKnow."
`;
  };

  const awsTrustPolicy = awsPrincipalArn && awsExternalId ? `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "${awsPrincipalArn}"
      },
      "Action": "sts:AssumeRole",
      "Condition": {
        "StringEquals": {
          "sts:ExternalId": "${awsExternalId}"
        }
      }
    }
  ]
}` : '';

  const isGcpMultiProjectScope = gcpScopeType !== 'project';
  const gcpScopeLabel = gcpScopeType === 'folder' ? 'Folder' : gcpScopeType === 'organization' ? 'Organization' : 'Projeto';
  const hasInvalidGcpBillingExportProjectId = Boolean(
    gcpBillingExportProjectId.trim() && !isValidGcpProjectId(gcpBillingExportProjectId)
  );
  const canContinueGcpSetup = Boolean(gcpSetupProjectId.trim()) && (!isGcpMultiProjectScope || Boolean(gcpScopeId.trim()));
  const canConnectGcp = isGcpMultiProjectScope
    ? Boolean(gcpSelectedProjectIds.length && gcpWorkloadIdentityProvider.trim() && gcpServiceAccountEmail.trim())
    : Boolean(gcpProjectId.trim() && gcpWorkloadIdentityProvider.trim() && gcpServiceAccountEmail.trim());

  return (
    <div className="connect-clouds">
      <div className="connect-clouds__header">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          <CloudOutlined className="mr-2" />
          Conectar Cloud Providers
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Conecte suas contas Cloud para análise de custos e otimização
        </p>
      </div>

      <Tabs defaultActiveKey="aws" size="large" className="connect-clouds__tabs">
        {/* AWS Tab */}
        <TabPane
          tab={
            <span>
              <AmazonOutlined />
              Amazon AWS
            </span>
          }
          key="aws"
        >
          <Card className="connect-clouds__card">
            <div className="space-y-4">
              <div className="connect-field-group">
                <label className="connect-field-label">Escopo da conexão</label>
                <Select value={awsMode} onChange={setAwsMode} className="w-full" size="large">
                  <Option value="individual">Conta AWS individual</Option>
                  <Option value="organization">AWS Organization (várias contas)</Option>
                </Select>
              </div>

              {awsMode === 'organization' ? (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="Onboarding centralizado com AWS Organizations"
                    description="Um External ID por cliente/organização e uma role por conta, distribuída automaticamente por CloudFormation StackSets."
                  />
                  <div className="connect-field-group">
                    <label className="connect-field-label">Nome do cliente *</label>
                    <Input value={awsCustomerName} onChange={event => setAwsCustomerName(event.target.value)} size="large" />
                  </div>
                  <div className="connect-field-group">
                    <label className="connect-field-label">Domínio do cliente</label>
                    <Input value={awsCustomerDomain} onChange={event => setAwsCustomerDomain(event.target.value)} size="large" placeholder="empresa.com.br" />
                  </div>
                  <div className="connect-field-group">
                    <label className="connect-field-label">Organization ID *</label>
                    <Input value={awsOrganizationId} onChange={event => setAwsOrganizationId(event.target.value)} size="large" placeholder="o-xxxxxxxxxx" />
                  </div>
                  <div className="connect-field-group">
                    <label className="connect-field-label">Management Account ID *</label>
                    <Input value={awsManagementAccountId} onChange={event => setAwsManagementAccountId(event.target.value)} size="large" placeholder="123456789012" />
                  </div>
                  {awsOrganizationOnboarding && (
                    <>
                      <div className="connect-field-group">
                        <label className="connect-field-label">External ID organizacional</label>
                        <Input value={awsOrganizationOnboarding.externalId} readOnly size="large" />
                      </div>
                      <Alert
                        type="warning"
                        showIcon
                        message="Já criou a FinOpsReadOnlyRole nas contas?"
                        description="Não recrie as roles. Atualize a trust policy existente em todas elas com o JSON abaixo. A role da Management Account também precisa permitir organizations:DescribeOrganization e organizations:ListAccounts."
                      />
                      <div className="connect-cli-panel">
                        <h4 className="connect-section-title">Trust policy para roles existentes</h4>
                        <div className="connect-code-row">
                          <pre>{JSON.stringify(awsOrganizationOnboarding.trustPolicy, null, 2)}</pre>
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(JSON.stringify(awsOrganizationOnboarding.trustPolicy, null, 2))}
                          />
                        </div>
                      </div>
                      <div className="connect-cli-panel">
                        <h4 className="connect-section-title">Template CloudFormation para o StackSet</h4>
                        <div className="connect-code-row">
                          <pre>{JSON.stringify(awsOrganizationOnboarding.stackSetTemplate, null, 2)}</pre>
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(JSON.stringify(awsOrganizationOnboarding.stackSetTemplate, null, 2))}
                          />
                        </div>
                        <p className="connect-cli-step__desc">
                          Na Management Account, crie um StackSet com permissões gerenciadas pelo serviço, selecione a Root/OU e habilite auto-deployment.
                        </p>
                      </div>
                    </>
                  )}
                  <Button
                    block
                    size="large"
                    onClick={discoverAwsOrganization}
                    loading={loading}
                    disabled={!awsOrganizationOnboarding || !/^\d{12}$/.test(awsManagementAccountId)}
                  >
                    Descobrir contas da organização
                  </Button>
                  {awsOrganizationAccounts.length > 0 && (
                    <div className="connect-field-group">
                      <label className="connect-field-label">Contas que serão conectadas</label>
                      <Select
                        mode="multiple"
                        value={selectedAwsOrganizationAccounts}
                        onChange={setSelectedAwsOrganizationAccounts}
                        className="w-full"
                        size="large"
                        optionFilterProp="label"
                        options={awsOrganizationAccounts.map(account => ({
                          value: account.id,
                          label: `${account.name || 'AWS Account'} — ${account.id}`
                        }))}
                      />
                    </div>
                  )}
                  <div className="connect-field-group">
                    <label className="connect-field-label">Região preferencial</label>
                    <Select value={awsRegion} onChange={setAwsRegion} className="w-full" size="large">
                      <Option value="us-east-1">US East (N. Virginia)</Option>
                      <Option value="us-east-2">US East (Ohio)</Option>
                      <Option value="us-west-2">US West (Oregon)</Option>
                      <Option value="sa-east-1">South America (São Paulo)</Option>
                    </Select>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    block
                    loading={loading}
                    disabled={!selectedAwsOrganizationAccounts.length}
                    onClick={connectAwsOrganization}
                    icon={<AmazonOutlined />}
                  >
                    Conectar {selectedAwsOrganizationAccounts.length || ''} contas
                  </Button>
                  {awsConnectionError && <Alert type="error" showIcon message={awsConnectionError} />}
                </>
              ) : (
                <>
              <div className="connect-field-group">
                <label className="connect-field-label">Método de Autenticação</label>
                <div className="connect-info-box connect-info-box--primary">
                  <div className="connect-info-box__title">AssumeRole (Seguro)</div>
                  <div className="connect-info-box__text">
                    Você cria uma Role na sua conta AWS que permite o FinOps acessá-la
                  </div>
                </div>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Nome do cliente *</label>
                <Input
                  placeholder="Ex.: Porto Seguro"
                  value={awsCustomerName}
                  onChange={(e) => setAwsCustomerName(e.target.value)}
                  size="large"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nome comercial exibido nos dashboards e relatórios
                </p>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Domínio do cliente</label>
                <Input
                  placeholder="empresa.com.br"
                  value={awsCustomerDomain}
                  onChange={(e) => setAwsCustomerDomain(e.target.value)}
                  size="large"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usado para identificar o cliente, agrupar conexões e carregar seu favicon
                </p>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Role ARN *</label>
                <Input
                  placeholder="arn:aws:iam::123456789012:role/FinOpsReadOnlyRole"
                  value={awsRoleArn}
                  onChange={(e) => setAwsRoleArn(e.target.value)}
                  size="large"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ARN da role IAM com permissões read-only
                </p>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">External ID *</label>
                <Input
                  placeholder="Gerado automaticamente após informar o Role ARN"
                  value={awsExternalId}
                  readOnly
                  size="large"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID exclusivo deste cliente e desta conexão
                </p>
                {awsPrincipalArn && (
                  <p className="text-xs text-gray-500 mt-1 break-all">Principal: {awsPrincipalArn}</p>
                )}
                {awsOnboardingError && <Alert message={awsOnboardingError} type="error" showIcon />}
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Região preferencial</label>
                <Select
                  value={awsRegion}
                  onChange={setAwsRegion}
                  className="w-full"
                  size="large"
                >
                  <Option value="us-east-1">US East (N. Virginia)</Option>
                  <Option value="us-east-2">US East (Ohio)</Option>
                  <Option value="us-west-1">US West (N. California)</Option>
                  <Option value="us-west-2">US West (Oregon)</Option>
                  <Option value="sa-east-1">South America (São Paulo)</Option>
                  <Option value="eu-west-1">Europe (Ireland)</Option>
                  <Option value="eu-central-1">Europe (Frankfurt)</Option>
                  <Option value="ap-southeast-1">Asia Pacific (Singapore)</Option>
                  <Option value="ap-northeast-1">Asia Pacific (Tokyo)</Option>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Usada como padrão na interface. O inventário completo descobre automaticamente todas as regiões habilitadas.
                </p>
              </div>

              <div className="connect-info-box">
                <h4 className="connect-section-title">
                  Segurança
                </h4>
                <div className="connect-list">
                  <div>Credenciais temporárias (1 hora)</div>
                  <div>Permissões read-only</div>
                  <div>Todas as ações no CloudTrail</div>
                  <div>External ID previne ataques</div>
                </div>
              </div>

              <Alert
                message="O cliente deve copiar a Trust Policy gerada pelo sistema"
                description={
                  <div className="connect-guide-prompt">
                    <span>Cole o JSON em IAM &gt; Roles &gt; FinOpsReadOnlyRole &gt; Trust relationships &gt; Edit trust policy.</span>
                    <Button
                      size="small"
                      onClick={() => navigate('/aws-setup')}
                      className="connect-guide-button"
                    >
                      <span>Ver assistente completo</span>
                      <ArrowRightOutlined />
                    </Button>
                  </div>
                }
                type="info"
                showIcon
                className="connect-guide-alert"
              />

              {awsTrustPolicy && (
                <div className="connect-cli-panel">
                  <h4 className="connect-section-title">
                    Trust Policy para colar na AWS
                  </h4>
                  <div className="connect-code-row">
                    <pre>{awsTrustPolicy}</pre>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(awsTrustPolicy)}
                      className="connect-copy-button"
                    />
                  </div>
                  <p className="connect-cli-step__desc">
                    O Principal é da plataforma FinOps e o External ID é único para esta conexão.
                  </p>
                </div>
              )}

              <div className="connect-cli-panel">
                <h4 className="connect-section-title">
                  Permissões que a role precisa ter
                </h4>
                <div className="connect-cli-list">
                  {[
                    { title: '1. Anexar ReadOnlyAccess', cmd: 'arn:aws:iam::aws:policy/ReadOnlyAccess', desc: 'Permite leitura dos recursos para inventário e recomendações.' },
                    { title: '2. Criar/anexar política FinOps', cmd: 'Cost Explorer + recomendações de Savings Plans/RI + Compute Optimizer para EC2/RDS', desc: 'Permite reconciliar custos, compromissos e rightsizing sem permissão de escrita.' }
                  ].map((item, idx) => (
                    <div className="connect-cli-step" key={idx}>
                      <div className="connect-cli-step__title">{item.title}</div>
                      <div className="connect-code-row">
                        <code>{item.cmd}</code>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(item.cmd)}
                          className="connect-copy-button"
                        />
                      </div>
                      <p className="connect-cli-step__desc">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="connect-info-box">
                <h4 className="connect-section-title">
                  Custos
                </h4>
                <div className="connect-list">
                  <div>Cost Explorer API: $0.01/requisição</div>
                  <div>Outras APIs: GRÁTIS</div>
                  <div>Estimativa: ~$0.30-$1.50/mês</div>
                  <div>Não cria recursos (só lê)</div>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                loading={loading}
                onClick={handleAWSConnect}
                icon={<AmazonOutlined />}
              >
                Conectar AWS
              </Button>
              {awsConnectionError && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  message="Não foi possível conectar a conta AWS"
                  description={awsConnectionError}
                  onClose={() => setAwsConnectionError('')}
                />
              )}
                </>
              )}
            </div>
          </Card>
        </TabPane>

        {/* Azure Tab */}
        <TabPane
          tab={
            <span>
              <CloudOutlined />
              Microsoft Azure
            </span>
          }
          key="azure"
        >
          <Card className="connect-clouds__card">
            <div className="space-y-4">
              <div className="connect-info-box">
                <h4 className="connect-section-title">
                  OAuth 2.0
                </h4>
                <div className="connect-list">
                  <div>Login seguro com Microsoft</div>
                  <div>Sem necessidade de credenciais</div>
                  <div>Acesso apenas às suas subscriptions</div>
                  <div>Permissões read-only</div>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                onClick={() => setAzureModalVisible(true)}
                icon={<CloudOutlined />}
              >
                Conectar com Microsoft Azure
              </Button>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Device Code Flow - Autenticação segura
              </p>
            </div>
          </Card>
        </TabPane>

        {/* GCP Tab */}
        <TabPane
          tab={
            <span>
              <CloudOutlined />
              Google Cloud
            </span>
          }
          key="gcp"
        >
          <Card className="connect-clouds__card">
            <div className="connect-gcp-wizard">
              <Alert
                message="Conexão segura, sem chave JSON"
                description="A xKnow usa Workload Identity Federation. O cliente não envia senha, arquivo de chave privada ou credencial permanente."
                type="success"
                showIcon
              />

              {gcpOnboardingError && (
                <Alert message={gcpOnboardingError} type="error" showIcon />
              )}

              <Steps
                current={gcpStep}
                className="connect-gcp-steps"
                items={[
                  { title: 'Escopo' },
                  { title: 'Cloud Shell' },
                  { title: 'Custos' },
                  { title: 'Conectar' }
                ]}
              />

              {gcpStep === 0 && (
                <div className="connect-gcp-step-panel">
                  <div className="connect-gcp-step-heading">
                    <span>Passo 1</span>
                    <h3>Escolha o escopo de conexão GCP</h3>
                    <p>
                      Para um único projeto, informe o Project ID. Para ambientes maiores, use Folder ou Organization e depois selecione os projetos encontrados.
                    </p>
                  </div>

                  <div className="connect-gcp-grid">
                    <div className="connect-field-group">
                      <label className="connect-field-label">Modo de conexão *</label>
                      <Select
                        value={gcpScopeType}
                        onChange={(value) => {
                          setGcpScopeType(value);
                          setGcpDiscoveredProjects([]);
                          setGcpSelectedProjectIds([]);
                        }}
                        size="large"
                        options={[
                          { value: 'project', label: 'Projeto único' },
                          { value: 'folder', label: 'Folder com múltiplos projetos' },
                          { value: 'organization', label: 'Organization enterprise' }
                        ]}
                      />
                      <p>
                        {isGcpMultiProjectScope
                          ? 'A xKnow vai descobrir os projetos acessíveis nesse escopo e deixar você escolher quais conectar.'
                          : 'Use este modo quando o cliente quer conectar apenas um projeto GCP.'}
                      </p>
                    </div>

                    <div className="connect-field-group">
                      <label className="connect-field-label">Projeto de setup *</label>
                      <Input
                        placeholder="meu-projeto-prod"
                        value={gcpSetupProjectId}
                        onChange={(event) => {
                          const value = event.target.value;
                          setGcpSetupProjectId(value);
                          setGcpProjectId(value);
                          setGcpBillingExportProjectId(current => current || value);
                        }}
                        size="large"
                      />
                      <p>
                        {isGcpMultiProjectScope
                          ? 'Projeto técnico onde serão criados a Service Account, Workload Identity Pool e Provider. Ele não define sozinho quais projetos serão analisados.'
                          : 'Projeto que será analisado e onde serão criados a Service Account, Workload Identity Pool e Provider.'}
                      </p>
                    </div>

                    <div className="connect-field-group">
                      <label className="connect-field-label">Nome do cliente</label>
                      <Input
                        placeholder="Nome da empresa"
                        value={gcpCustomerName}
                        onChange={(event) => setGcpCustomerName(event.target.value)}
                        size="large"
                      />
                      <p>Usado no cabeçalho do dashboard e nos relatórios executivos.</p>
                    </div>

                    <div className="connect-field-group">
                      <label className="connect-field-label">Domínio do cliente</label>
                      <Input
                        placeholder="empresa.com.br"
                        value={gcpCustomerDomain}
                        onChange={(event) => setGcpCustomerDomain(event.target.value)}
                        size="large"
                      />
                      <p>A xKnow tenta carregar a logo pública automaticamente a partir do domínio.</p>
                    </div>

                    <div className="connect-field-group">
                      <label className="connect-field-label">Billing Account ID</label>
                      <Input
                        placeholder="012345-ABCDEF-678901"
                        value={gcpSetupBillingAccountId}
                        onChange={(event) => setGcpSetupBillingAccountId(event.target.value)}
                        size="large"
                      />
                      <p>Opcional agora, mas recomendado para permitir análise financeira completa.</p>
                    </div>

                    <div className="connect-field-group">
                      <label className="connect-field-label">Service Account Email ou ID</label>
                      <Input
                        placeholder="xknow-finops-reader@meu-projeto-prod.iam.gserviceaccount.com"
                        value={gcpSetupServiceAccountEmail}
                        onChange={(event) => handleGcpSetupServiceAccountEmailChange(event.target.value)}
                        size="large"
                      />
                      <p>
                        Se o cliente já criou uma Service Account, cole o e-mail completo. Se deixar vazio, o script cria xknow-finops-readonly no projeto de setup.
                      </p>
                    </div>

                    {gcpScopeType !== 'project' && (
                      <div className="connect-field-group">
                        <label className="connect-field-label">
                          {gcpScopeType === 'folder' ? 'Folder ID *' : 'Organization ID *'}
                        </label>
                        <Input
                          placeholder={gcpScopeType === 'folder' ? '123456789012' : '987654321098'}
                          value={gcpScopeId}
                          onChange={(event) => {
                            setGcpScopeId(event.target.value);
                            setGcpDiscoveredProjects([]);
                            setGcpSelectedProjectIds([]);
                          }}
                          size="large"
                        />
                        <p>Informe apenas o número. A xKnow usará leitura herdada para descobrir projetos.</p>
                      </div>
                    )}
                  </div>

                  <div className="connect-info-box">
                    <div className="connect-info-box__title">O que será criado no GCP?</div>
                    <div className="connect-list">
                      <div>Service Account somente leitura para a xKnow.</div>
                      <div>Workload Identity Pool e Provider para confiar apenas na role AWS da plataforma.</div>
                      <div>Permissões de leitura para inventário, billing e BigQuery no escopo escolhido.</div>
                    </div>
                  </div>

                  <div className="connect-wizard-actions">
                    <Button
                      type="primary"
                      disabled={!canContinueGcpSetup}
                      onClick={() => setGcpStep(1)}
                    >
                      Continuar para o script
                    </Button>
                  </div>
                </div>
              )}

              {gcpStep === 1 && (
                <div className="connect-gcp-step-panel">
                  <div className="connect-gcp-step-heading">
                    <span>Passo 2</span>
                    <h3>Rode o script no Cloud Shell do Google</h3>
                    <p>
                      Copie o script abaixo, abra o Cloud Shell no Google Cloud e cole tudo de uma vez. No final, ele imprime os 3 campos que você vai colar na xKnow.
                    </p>
                  </div>

                  <div className="connect-gcp-grid connect-gcp-grid--three">
                    <div className="connect-field-group">
                      <label className="connect-field-label">Workload Identity Pool ID</label>
                      <Input
                        value={gcpSetupPoolId}
                        onChange={(event) => setGcpSetupPoolId(event.target.value)}
                        size="large"
                      />
                    </div>
                    <div className="connect-field-group">
                      <label className="connect-field-label">Provider ID</label>
                      <Input
                        value={gcpSetupProviderId}
                        onChange={(event) => setGcpSetupProviderId(event.target.value)}
                        size="large"
                      />
                    </div>
                    <div className="connect-field-group">
                      <label className="connect-field-label">Service Account Email ou ID</label>
                      <Input
                        placeholder="xknow-finops-reader@meu-projeto-prod.iam.gserviceaccount.com"
                        value={gcpSetupServiceAccountEmail}
                        onChange={(event) => handleGcpSetupServiceAccountEmailChange(event.target.value)}
                        size="large"
                      />
                      <p>
                        Se o cliente já criou uma Service Account, cole o e-mail completo. Se deixar vazio, o script cria xknow-finops-readonly no projeto de setup.
                      </p>
                    </div>
                  </div>

                  {gcpOnboarding?.awsRoleArn && (
                    <div className="connect-info-box">
                      <div className="connect-info-box__title">Identidade da xKnow usada na federação</div>
                      <div className="connect-code-row">
                        <code>{gcpOnboarding.awsAssumedRoleArn || gcpOnboarding.awsRoleArn}</code>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(gcpOnboarding.awsAssumedRoleArn || gcpOnboarding.awsRoleArn)}
                          className="connect-copy-button"
                        />
                      </div>
                      <p className="connect-cli-step__desc">
                        O script usa essa identidade na condição do Provider, impedindo outro principal AWS de assumir a Service Account.
                      </p>
                    </div>
                  )}

                  <div className="connect-code-row connect-code-row--block">
                    <code>{buildGcpSetupScript()}</code>
                  </div>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(buildGcpSetupScript())}
                    block
                  >
                    Copiar script para Cloud Shell
                  </Button>

                  <div className="connect-wizard-actions">
                    <Button onClick={() => setGcpStep(0)}>Voltar</Button>
                    <Button type="primary" onClick={() => setGcpStep(2)}>
                      Já rodei o script
                    </Button>
                  </div>
                </div>
              )}

              {gcpStep === 2 && (
                <div className="connect-gcp-step-panel">
                  <div className="connect-gcp-step-heading">
                    <span>Passo 3</span>
                    <h3>Informe onde estão os dados de custo</h3>
                    <p>
                      O Google Cloud só entrega histórico financeiro completo via Cloud Billing Export para BigQuery. Você pode salvar a conexão agora e preencher esses campos depois.
                    </p>
                  </div>

                  <Alert
                    message="Billing Export é necessário para dashboard financeiro completo"
                    description="Sem o export, a xKnow salva a conexão e valida o projeto, mas os gráficos de custo histórico ficam limitados."
                    type="info"
                    showIcon
                  />

                  {hasInvalidGcpBillingExportProjectId && (
                    <Alert
                      message="Projeto do Billing Export inválido"
                      description="Informe um Project ID GCP válido, como meu-projeto-prod, ou deixe o campo vazio para usar o projeto de setup."
                      type="warning"
                      showIcon
                    />
                  )}

                  <div className="connect-gcp-grid connect-gcp-grid--three">
                    <div className="connect-field-group">
                      <label className="connect-field-label">Projeto do Billing Export</label>
                      <Input
                        placeholder="billing-export-project"
                        value={gcpBillingExportProjectId}
                        onChange={(event) => setGcpBillingExportProjectId(event.target.value)}
                        size="large"
                      />
                    </div>
                    <div className="connect-field-group">
                      <label className="connect-field-label">Dataset</label>
                      <Input
                        placeholder="billing_export"
                        value={gcpBillingExportDataset}
                        onChange={(event) => setGcpBillingExportDataset(event.target.value)}
                        size="large"
                      />
                    </div>
                    <div className="connect-field-group">
                      <label className="connect-field-label">Tabela</label>
                      <Input
                        placeholder="gcp_billing_export_v1_XXXXXX"
                        value={gcpBillingExportTable}
                        onChange={(event) => setGcpBillingExportTable(event.target.value)}
                        size="large"
                      />
                    </div>
                  </div>

                  <div className="connect-wizard-actions">
                    <Button onClick={() => setGcpStep(1)}>Voltar</Button>
                    <Button
                      type="primary"
                      disabled={hasInvalidGcpBillingExportProjectId}
                      onClick={() => setGcpStep(3)}
                    >
                      Continuar para conexão
                    </Button>
                  </div>
                </div>
              )}

              {gcpStep === 3 && (
                <div className="connect-gcp-step-panel">
                  <div className="connect-gcp-step-heading">
                    <span>Passo 4</span>
                    <h3>Cole os campos finais e valide a conexão</h3>
                    <p>
                      {isGcpMultiProjectScope
                        ? `Depois de rodar o script, cole o Provider e a Service Account, descubra os projetos do ${gcpScopeLabel} e selecione quais conectar.`
                        : 'Depois de rodar o script, copie os valores impressos no final do Cloud Shell e cole abaixo.'}
                    </p>
                  </div>

                  {!isGcpMultiProjectScope && (
                    <div className="connect-field-group">
                      <label className="connect-field-label">Project ID *</label>
                      <Input
                        placeholder="meu-projeto-prod"
                        value={gcpProjectId}
                        onChange={(event) => setGcpProjectId(event.target.value)}
                        size="large"
                      />
                    </div>
                  )}

                  {isGcpMultiProjectScope && (
                    <div className="connect-field-group">
                      <label className="connect-field-label">
                        {gcpScopeType === 'folder' ? 'Folder ID' : 'Organization ID'}
                      </label>
                      <Input
                        placeholder={gcpScopeType === 'folder' ? '123456789012' : '987654321098'}
                        value={gcpScopeId}
                        onChange={(event) => {
                          setGcpScopeId(event.target.value);
                          setGcpDiscoveredProjects([]);
                          setGcpSelectedProjectIds([]);
                        }}
                        size="large"
                      />
                      <p>Este é o escopo usado para descobrir projetos. Não é necessário informar Project ID individual neste modo.</p>
                    </div>
                  )}

                  <div className="connect-field-group">
                    <label className="connect-field-label">Workload Identity Provider *</label>
                    <Input
                      placeholder="//iam.googleapis.com/projects/123456789/locations/global/workloadIdentityPools/xknow-finops-pool/providers/xknow-aws-provider"
                      value={gcpWorkloadIdentityProvider}
                      onChange={(event) => setGcpWorkloadIdentityProvider(event.target.value)}
                      size="large"
                    />
                    <p>Campo impresso pelo script como "Workload Identity Provider".</p>
                  </div>

                  <div className="connect-field-group">
                    <label className="connect-field-label">Service Account Email *</label>
                    <Input
                      placeholder="xknow-finops-readonly@meu-projeto-prod.iam.gserviceaccount.com"
                      value={gcpServiceAccountEmail}
                      onChange={(event) => handleGcpServiceAccountEmailChange(event.target.value)}
                      size="large"
                    />
                    <p>Campo impresso pelo script como "Service Account Email".</p>
                  </div>

                  <div className="connect-info-box">
                    <div className="connect-info-box__title">Antes de conectar, confira:</div>
                    <div className="connect-list">
                      <div>O script terminou com "Concluído".</div>
                      <div>
                        {isGcpMultiProjectScope
                          ? 'O Provider e a Service Account foram copiados do final do Cloud Shell.'
                          : 'O Project ID, Provider e Service Account foram copiados do final do Cloud Shell.'}
                      </div>
                      <div>Se quiser custos históricos, o Billing Export foi configurado no passo anterior.</div>
                    </div>
                  </div>

                  {isGcpMultiProjectScope && (
                    <div className="connect-info-box">
                      <div className="connect-info-box__title">Seleção de projetos</div>
                      <p className="connect-cli-step__desc">
                        Descubra os projetos acessíveis no escopo e selecione quais devem virar conexões na xKnow.
                      </p>
                      <Button
                        onClick={handleGCPDiscoverProjects}
                        loading={discoveringGcpProjects}
                        block
                      >
                        Descobrir projetos GCP
                      </Button>
                      {gcpDiscoveredProjects.length > 0 && (
                        <div className="connect-field-group" style={{ marginTop: 16 }}>
                          <label className="connect-field-label">Projetos selecionados</label>
                          <Select
                            mode="multiple"
                            value={gcpSelectedProjectIds}
                            onChange={setGcpSelectedProjectIds}
                            size="large"
                            options={gcpDiscoveredProjects.map(project => ({
                              value: project.projectId,
                              label: `${project.name || project.projectId} (${project.projectId})`
                            }))}
                            style={{ width: '100%' }}
                            maxTagCount="responsive"
                          />
                          <p>{gcpSelectedProjectIds.length} de {gcpDiscoveredProjects.length} projeto(s) selecionado(s).</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="connect-wizard-actions">
                    <Button onClick={() => setGcpStep(2)}>Voltar</Button>
                    <Button
                      type="primary"
                      size="large"
                      loading={loading}
                      onClick={handleGCPConnect}
                      icon={<CloudOutlined />}
                      disabled={!canConnectGcp}
                    >
                      Conectar Google Cloud
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabPane>
      </Tabs>

      <Card className="connect-clouds__docs" title="Boas práticas e referências">
        <div className="connect-doc-summary">
          <p>
            As integrações usam acesso de menor privilégio, credenciais temporárias
            e fluxos oficiais dos provedores. No GCP, usamos Workload Identity Federation
            para evitar chaves permanentes de Service Account.
          </p>
          <a
            href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_create.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Referência AWS IAM Role
          </a>
        </div>
      </Card>
      
      {/* Modal Azure Device Code */}
      <Modal
        title="Conectar Microsoft Azure"
        open={azureModalVisible}
        onCancel={() => {
          setAzureModalVisible(false);
          setDeviceCodeData(null);
          setPolling(false);
          setAzureAuthCompleted(false);
          setAzureSubscriptions([]);
          setSelectedAzureSubscriptions([]);
          setSelectedAzureDirectory('');
          setAzureCustomerName('');
          setAzureCustomerDomain('');
        }}
        footer={null}
        width={600}
      >
        {!deviceCodeData ? (
          <div className="text-center py-4">
            <Alert
              message="Autenticação Segura"
              description="Vamos usar o Device Code Flow da Microsoft para autenticação segura."
              type="info"
              showIcon
              className="mb-4"
            />

            <div className="connect-field-group">
              <label className="connect-field-label">Nome do cliente *</label>
              <Input
                value={azureCustomerName}
                onChange={(event) => setAzureCustomerName(event.target.value)}
                placeholder="Ex.: Vidalink"
                size="large"
              />
            </div>

            <div className="connect-field-group">
              <label className="connect-field-label">Domínio do cliente</label>
              <Input
                value={azureCustomerDomain}
                onChange={(event) => setAzureCustomerDomain(event.target.value)}
                placeholder="empresa.com.br"
                size="large"
              />
              <p className="text-xs text-gray-500 mt-1">
                Todas as subscriptions selecionadas serão agrupadas neste cliente.
              </p>
            </div>
            
            <Button 
              type="primary" 
              onClick={handleAzureConnect}
              loading={loading}
              size="large"
            >
              {loading ? 'Gerando código...' : 'Iniciar Autenticação'}
            </Button>
          </div>
        ) : azureAuthCompleted && azureSubscriptions.length ? (
          <div className="space-y-4">
            <Alert
              message="Selecione o Directory e os ambientes Azure"
              description="Este login pode ter acesso a vários clientes. Primeiro escolha o Directory, depois marque uma ou mais subscriptions daquele escopo."
              type="success"
              showIcon
            />

            {(() => {
              const directoryMap = new Map<string, { id: string; name: string; count: number }>();
              azureSubscriptions.forEach(subscription => {
                const directoryId = getAzureDirectoryId(subscription);
                const directoryName = getAzureDirectoryName(subscription);
                const current = directoryMap.get(directoryId);
                directoryMap.set(directoryId, {
                  id: directoryId,
                  name: directoryName,
                  count: (current?.count || 0) + 1
                });
              });
              const directories = Array.from(directoryMap.values());

              return (
                <div className="connect-azure-directory-block">
                  <label className="connect-field-label">Directory / Tenant</label>
                  <Select
                    value={selectedAzureDirectory}
                    onChange={(directoryId) => {
                      setSelectedAzureDirectory(directoryId);
                      setSelectedAzureSubscriptions(
                        azureSubscriptions
                          .filter(subscription => getAzureDirectoryId(subscription) === directoryId)
                          .map(getAzureSubscriptionId)
                          .filter(Boolean)
                      );
                    }}
                    size="large"
                    className="w-full"
                    placeholder="Selecione o Directory"
                  >
                    {directories.map(directory => (
                      <Option key={directory.id} value={directory.id}>
                        {directory.name} ({directory.count} subscription{directory.count === 1 ? '' : 's'})
                      </Option>
                    ))}
                  </Select>
                </div>
              );
            })()}

            <Select
              mode="multiple"
              value={selectedAzureSubscriptions}
              onChange={setSelectedAzureSubscriptions}
              placeholder="Selecione uma ou mais subscriptions"
              size="large"
              className="w-full"
              maxTagCount="responsive"
            >
              {azureSubscriptions
                .filter(subscription => !selectedAzureDirectory || getAzureDirectoryId(subscription) === selectedAzureDirectory)
                .map(subscription => {
                  const subscriptionId = getAzureSubscriptionId(subscription);
                  return (
                    <Option key={subscriptionId} value={subscriptionId}>
                      {subscription.name || 'Azure Subscription'} - {subscriptionId}
                    </Option>
                  );
                })}
            </Select>

            <div className="connect-selection-actions">
              <Button size="small" onClick={handleSelectAllAzureSubscriptions}>
                Selecionar todas
              </Button>
              <Button size="small" onClick={handleClearAzureSubscriptions}>
                Limpar seleção
              </Button>
              <span>
                {selectedAzureSubscriptions.length} de {
                  azureSubscriptions.filter(subscription => !selectedAzureDirectory || getAzureDirectoryId(subscription) === selectedAzureDirectory).length
                } selecionadas
              </span>
            </div>

            <Button
              type="primary"
              size="large"
              block
              loading={savingAzureSubscriptions}
              onClick={handleSelectAzureSubscriptions}
              disabled={!selectedAzureSubscriptions.length || !azureCustomerName.trim()}
            >
              Conectar ambientes selecionados
            </Button>
          </div>
        ) : azureAuthCompleted ? (
          <div className="space-y-4">
            <Alert
              message="Nenhuma subscription Azure encontrada"
              description="O login Microsoft foi concluído, mas este usuário não retornou subscriptions acessíveis para conectar."
              type="warning"
              showIcon
            />
            <Button
              block
              onClick={() => {
                setDeviceCodeData(null);
                setAzureAuthCompleted(false);
                setAzureSubscriptions([]);
                setSelectedAzureSubscriptions([]);
                setSelectedAzureDirectory('');
              }}
            >
              Tentar novamente
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert
              message="Siga os passos para autenticar"
              description="Copie o código abaixo e cole no site da Microsoft."
              type="warning"
              showIcon
            />
            
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-center border border-gray-300 dark:border-gray-600">
              <div className="text-3xl font-bold tracking-widest text-gray-900 dark:text-white mb-2">
                {deviceCodeData.userCode}
              </div>
              <Button 
                type="link" 
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(deviceCodeData.userCode)}
              >
                Copiar Código
              </Button>
            </div>
            
            <div className="text-center">
              <a 
                href={AZURE_DEVICE_LOGIN_URL} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-lg"
              >
                Abrir página de autenticação da Microsoft
              </a>
            </div>
            
            {polling && (
              <Alert
                message="Aguardando autenticação..."
                description="Cole o código na página da Microsoft e faça login."
                type="info"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ConnectClouds;
